import { useCallback, useEffect, useState } from 'react';
import { FlatList, StyleSheet, View, type ListRenderItemInfo } from 'react-native';
import { useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ApiError } from '../../api/client';
import { getStudyPlan } from '../../api/studyPlan';
import { AddTaskSheet } from '../../components/business/AddTaskSheet';
import { PlanAiSuggestionCard } from '../../components/business/PlanAiSuggestionCard';
import { PlanProgressCard } from '../../components/business/PlanProgressCard';
import { StudyPlanTaskCard } from '../../components/business/StudyPlanTaskCard';
import { WeekCalendarStrip } from '../../components/business/WeekCalendarStrip';
import { AppText } from '../../components/ui/AppText';
import { EmptyView } from '../../components/ui/EmptyView';
import { ErrorView } from '../../components/ui/ErrorView';
import { LoadingView } from '../../components/ui/LoadingView';
import { showAppToast } from '../../components/ui/AppToast';
import { tokens } from '../../theme/tokens';
import type {
  StudyPlanTaskDraft,
  StudyPlanTaskViewModel,
  StudyPlanViewModel,
} from '../../types/studyPlan';

type StudyPlanPageState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'empty' }
  | { status: 'success'; plan: StudyPlanViewModel };

const isPlanEmpty = (plan: StudyPlanViewModel): boolean => plan.tasks.length === 0;

const getErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.code === 'unauthorized') {
      return '登录状态已失效，请重新登录';
    }
    return error.message;
  }
  return '暂时无法加载学习计划';
};

export default function StudyPlanTabPage() {
  const router = useRouter();
  const [state, setState] = useState<StudyPlanPageState>({ status: 'loading' });
  const [selectedDateId, setSelectedDateId] = useState<string | null>(null);
  const [isAddSheetVisible, setAddSheetVisible] = useState(false);
  const [taskName, setTaskName] = useState('');
  const [taskSubject, setTaskSubject] = useState('数学');
  const [taskMinutes, setTaskMinutes] = useState(25);
  const [taskDate, setTaskDate] = useState('');

  const loadPlan = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      const plan = await getStudyPlan();
      if (isPlanEmpty(plan)) {
        setState({ status: 'empty' });
        return;
      }
      setSelectedDateId(plan.selectedDateId);
      setTaskDate(plan.selectedDateId);
      setState({ status: 'success', plan });
    } catch (error: unknown) {
      setState({ status: 'error', message: getErrorMessage(error) });
    }
  }, []);

  useEffect(() => {
    void loadPlan();
  }, [loadPlan]);

  const openTask = useCallback(
    (task: StudyPlanTaskViewModel) => {
      if (task.routeKey === 'photo-search') {
        router.push('/photo-search');
      }
      if (task.routeKey === 'wrong-question-practice') {
        router.push('/wrong-question-practice');
      }
    },
    [router]
  );

  const recalculatePlan = useCallback((plan: StudyPlanViewModel): StudyPlanViewModel => {
    const completedCount = plan.tasks.filter((task) => task.isCompleted).length;
    const totalCount = plan.tasks.length;
    return {
      ...plan,
      completedCount,
      totalCount,
      completionPercent: totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0,
    };
  }, []);

  const toggleTaskCompletion = useCallback(
    (taskId: string) => {
      setState((current) => {
        if (current.status !== 'success') {
          return current;
        }
        const tasks = current.plan.tasks.map((task) => {
          if (task.id !== taskId) {
            return task;
          }
          const isCompleted = !task.isCompleted;
          return {
            ...task,
            isCompleted,
            status: isCompleted ? 'completed' : 'pending',
            statusLabel: isCompleted ? '已完成' : '待完成',
          } satisfies StudyPlanTaskViewModel;
        });
        return { status: 'success', plan: recalculatePlan({ ...current.plan, tasks }) };
      });
    },
    [recalculatePlan]
  );

  const showDevelopmentToast = useCallback((featureName: string) => {
    showAppToast(`${featureName}功能开发中`, 'info');
  }, []);

  const appendLocalTask = useCallback(
    (taskDraft: StudyPlanTaskDraft) => {
      if (!taskDraft.title.trim()) {
        return;
      }

      setState((current) => {
        if (current.status !== 'success') {
          return current;
        }
        const nextTask: StudyPlanTaskViewModel = {
          id: `local-task-${Date.now()}`,
          title: taskDraft.title.trim(),
          subject: taskDraft.subject.trim() || '综合',
          estimatedMinutes: taskDraft.estimatedMinutes,
          dateId: taskDraft.dateId,
          status: 'pending',
          isCompleted: false,
          statusLabel: '待完成',
          rewardLabel: taskDraft.rewardLabel ?? '+1',
          routeKey: taskDraft.routeKey ?? 'photo-search',
        };
        return {
          status: 'success',
          plan: recalculatePlan({ ...current.plan, tasks: [nextTask, ...current.plan.tasks] }),
        };
      });
      showAppToast('已加入今日计划', 'success');
    },
    [recalculatePlan]
  );

  const submitTask = useCallback(() => {
    if (!taskName.trim()) {
      return;
    }
    appendLocalTask({
      title: taskName,
      subject: taskSubject,
      estimatedMinutes: taskMinutes,
      dateId: taskDate || selectedDateId || '',
      routeKey: 'photo-search',
      rewardLabel: '+1',
    });
    setTaskName('');
    setTaskSubject('数学');
    setTaskMinutes(25);
    setAddSheetVisible(false);
  }, [appendLocalTask, selectedDateId, taskDate, taskMinutes, taskName, taskSubject]);

  if (state.status === 'loading') {
    return (
      <SafeAreaView style={styles.screen}>
        <LoadingView message="正在加载学习计划" />
      </SafeAreaView>
    );
  }

  if (state.status === 'error') {
    return (
      <SafeAreaView style={styles.screen}>
        <ErrorView message={state.message} onRetry={loadPlan} />
      </SafeAreaView>
    );
  }

  if (state.status === 'empty') {
    return (
      <SafeAreaView style={styles.screen}>
        <EmptyView
          title="暂无学习计划"
          description="后续会根据薄弱点和目标自动生成今日计划。"
          actionLabel="重新加载"
          onAction={loadPlan}
        />
      </SafeAreaView>
    );
  }

  const { plan } = state;
  const activeDateId = selectedDateId ?? plan.selectedDateId;
  const aiPlanSuggestions: StudyPlanTaskDraft[] = [
    {
      title: '几何薄弱点专项练习',
      subject: '数学',
      estimatedMinutes: 20,
      dateId: activeDateId,
      routeKey: 'photo-search',
      rewardLabel: '+2',
    },
    {
      title: '错题本快速复盘',
      subject: '综合',
      estimatedMinutes: 15,
      dateId: activeDateId,
      routeKey: 'wrong-question-practice',
      rewardLabel: '+2',
    },
  ];

  const renderTask = ({ item }: ListRenderItemInfo<StudyPlanTaskViewModel>) => (
    <StudyPlanTaskCard
      title={item.title}
      subject={item.subject}
      estimatedMinutes={item.estimatedMinutes}
      isCompleted={item.isCompleted}
      statusLabel={item.statusLabel}
      rewardLabel={item.rewardLabel}
      onToggleComplete={() => toggleTaskCompletion(item.id)}
      onPress={() => openTask(item)}
    />
  );

  return (
    <SafeAreaView style={styles.screen}>
      <FlatList
        data={plan.tasks}
        keyExtractor={(item) => item.id}
        renderItem={renderTask}
        contentContainerStyle={styles.content}
        ListHeaderComponent={
          <View style={styles.header}>
            <AppText variant="title" weight="bold">
              学习计划
            </AppText>
            <WeekCalendarStrip
              days={plan.days}
              selectedDateId={activeDateId}
              onSelectDate={(dateId) => {
                setSelectedDateId(dateId);
                setTaskDate(dateId);
              }}
              onPreviousWeek={() => showDevelopmentToast('上一周')}
              onNextWeek={() => showDevelopmentToast('下一周')}
              onOpenMonthPicker={() => showDevelopmentToast('月份选择')}
            />
            <PlanProgressCard
              completionPercent={plan.completionPercent}
              completedCount={plan.completedCount}
              totalCount={plan.totalCount}
              onAddTask={() => setAddSheetVisible(true)}
            />
            <PlanAiSuggestionCard
              suggestions={aiPlanSuggestions}
              onUseSuggestion={appendLocalTask}
            />
            <AppText variant="caption" tone="muted">
              今日任务 {plan.completedCount}/{plan.totalCount} · 长按拖动可排序
            </AppText>
          </View>
        }
        ItemSeparatorComponent={Separator}
      />
      <AddTaskSheet
        visible={isAddSheetVisible}
        taskName={taskName}
        subject={taskSubject}
        estimatedMinutes={taskMinutes}
        taskDate={taskDate || activeDateId}
        onChangeTaskName={setTaskName}
        onChangeSubject={setTaskSubject}
        onChangeEstimatedMinutes={setTaskMinutes}
        onChangeTaskDate={setTaskDate}
        onSubmit={submitTask}
        onClose={() => setAddSheetVisible(false)}
      />
    </SafeAreaView>
  );
}

function Separator() {
  return <View style={styles.separator} />;
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: tokens.colors.background,
  },
  content: {
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.lg,
    paddingBottom: tokens.spacing.xxl,
  },
  header: {
    gap: tokens.spacing.md,
    marginBottom: tokens.spacing.md,
  },
  separator: {
    height: tokens.spacing.md,
  },
});
